/**
 * AdminErrorBoundary — catches uncaught render errors inside the admin shell
 * so a single broken component (e.g. the iter166 `bulk is not defined` bug)
 * shows a friendly fallback + auto-reports to the Site Health error sink
 * instead of a blank page.
 *
 * Also installs a one-time global listener for window.onerror and
 * unhandledrejection so non-React errors (loose script bugs, fetch failures)
 * are captured too. Both paths POST to /api/site-health/log-error.
 */
import { Component } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

// Install global handlers exactly once (HMR-safe).
let _globalInstalled = false;
const installGlobalErrorHandlers = () => {
  if (_globalInstalled || typeof window === 'undefined') return;
  _globalInstalled = true;

  const post = (body) => {
    try {
      axios.post(`${API}/api/site-health/log-error`, body, { timeout: 4000 })
        .catch(() => { /* swallow — logger must never break the page */ });
    } catch (_) {}
  };

  window.addEventListener('error', (e) => {
    if (!e || !e.message) return;
    post({
      message: String(e.message).slice(0, 2000),
      source: 'window',
      stack: e.error?.stack ? String(e.error.stack).slice(0, 8000) : '',
      url: window.location.href,
      user_agent: navigator.userAgent,
      route: window.location.pathname,
    });
  });

  window.addEventListener('unhandledrejection', (e) => {
    const reason = e?.reason;
    const msg = reason?.message || String(reason || 'Unhandled promise rejection');
    post({
      message: msg.slice(0, 2000),
      source: 'promise',
      stack: reason?.stack ? String(reason.stack).slice(0, 8000) : '',
      url: window.location.href,
      user_agent: navigator.userAgent,
      route: window.location.pathname,
    });
  });
};

class AdminErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, info: null };
    installGlobalErrorHandlers();
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    this.setState({ info });
    try {
      axios.post(`${API}/api/site-health/log-error`, {
        message: String(error?.message || 'Unknown render error').slice(0, 2000),
        source: 'react',
        stack: String(error?.stack || '').slice(0, 8000),
        component_stack: String(info?.componentStack || '').slice(0, 4000),
        url: window.location.href,
        user_agent: navigator.userAgent,
        route: window.location.pathname,
      }, { timeout: 4000 }).catch(() => {});
    } catch (_) {}
  }

  reset = () => this.setState({ hasError: false, error: null, info: null });

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div
        className="min-h-screen bg-background flex items-center justify-center px-5 py-12"
        data-testid="admin-error-boundary"
      >
        <div className="max-w-lg w-full bg-zinc-950 border border-amber-500/30 rounded-2xl p-7">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-11 h-11 rounded-full bg-amber-500/15 flex items-center justify-center shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-400" />
            </div>
            <div className="min-w-0">
              <h1 className="font-heading font-extrabold text-xl uppercase tracking-tight">
                Something broke in the admin
              </h1>
              <p className="text-sm text-zinc-400 mt-1">
                We've logged the error to Site Health. Try reloading or head back
                to the dashboard.
              </p>
            </div>
          </div>

          <div className="bg-black/40 border border-white/10 rounded-xl p-3 mb-5 max-h-40 overflow-auto">
            <p className="text-[11px] font-mono text-amber-300 break-all">
              {String(this.state.error?.message || 'Unknown error')}
            </p>
          </div>

          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => window.location.reload()}
              className="px-4 h-10 rounded-full bg-rastyle-orange text-black font-bold uppercase tracking-wide text-xs inline-flex items-center gap-2 hover:bg-orange-400 transition-colors"
              data-testid="admin-error-reload"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Reload
            </button>
            <button
              onClick={() => { this.reset(); window.location.assign('/admin'); }}
              className="px-4 h-10 rounded-full bg-white/5 hover:bg-white/10 text-white font-bold uppercase tracking-wide text-xs inline-flex items-center gap-2 transition-colors"
              data-testid="admin-error-home"
            >
              <Home className="w-3.5 h-3.5" /> Back to /admin
            </button>
          </div>
        </div>
      </div>
    );
  }
}

export default AdminErrorBoundary;
